import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import TextAreaInput from "components/ui/input/TextAreaInput";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { useUpdateTeamDefaultSearchTopic } from "lib/hooks/api/teams/useUpdateTeamDefaultSearchTopic";

type EditDefaultSearchTopicModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultSearchTopic: string;
  onSuccess?: (topic: string) => void;
};

export function EditDefaultSearchTopicModal({
  open,
  onOpenChange,
  defaultSearchTopic,
  onSuccess,
}: EditDefaultSearchTopicModalProps) {
  const [topic, setTopic] = useState(defaultSearchTopic);

  useEffect(() => {
    if (open) {
      setTopic(defaultSearchTopic);
    }
  }, [open, defaultSearchTopic]);

  const { mutate: updateDefaultSearchTopic, isLoading } = useUpdateTeamDefaultSearchTopic({
    onSuccess: (data) => {
      const updatedTopic = data.updateTeamDefaultSearchTopic.defaultSearchTopic?.trim() ?? "";
      toast.success("Default topic updated");
      onOpenChange(false);
      onSuccess?.(updatedTopic);
    },
    onError: () => {
      toast.error("Failed to update default topic");
    },
  });

  const trimmedTopic = topic.trim();
  const trimmedDefault = defaultSearchTopic.trim();
  const isUnchanged = trimmedTopic === trimmedDefault;

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setTopic(defaultSearchTopic);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateDefaultSearchTopic({
      input: { defaultSearchTopic: topic },
    });
  };

  return (
    <Modal open={open} onOpenChange={handleOpenChange} maxWidth="480px">
      <form onSubmit={handleSubmit} aria-label="Edit default topic">
        <Flex column gap={16}>
          <Flex column gap={8}>
            <Text h2>Default topic</Text>
            <Description>
              Set your default topic that will be applied to all new searches.
            </Description>
          </Flex>
          <TextAreaInput
            name="defaultSearchTopic"
            value={topic}
            onChange={(e) => setTopic(e.currentTarget.value)}
            minRows={4}
            autoComplete="off"
            aria-label="Default topic"
            disabled={isLoading}
          />
          <Flex gap={8} justifyContent="flex-end">
            <Button
              buttonType="secondary"
              type="button"
              onClick={() => handleOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" loading={isLoading} disabled={isLoading || isUnchanged}>
              Save changes
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
}

const Description = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
  fontSize: "14px",
  lineHeight: "22px",
}));
